import {
  SwitchButton,
  TextSizes,
  stylesSwitchButtonRect,
} from "@smartsuite/react-ui/lib";
import { t } from "i18next";
import { observer } from "mobx-react-lite";
import { useEffect, useState } from "react";
import { CaptionBlock } from "../../../../../../../../../components/CaptionBlock";
import type {
  ActionCandidate,
  InputCandidate,
} from "../../../../../../../../../types/candidate";
import {
  FIND_RECORDS_LIMIT,
  FIND_RECORD_LIMIT_ID,
  FIND_RECORD_LIMIT_SORT_ID,
  INPUT_ACTION_NAME_ID,
  INPUT_APPLICATION_ID,
  INPUT_SOLUTION_ID,
} from "../../../../../../../../../utils/constants";
import {
  findInputAndPick,
  pickValueConfigString,
} from "../../../../../../../../../utils/inputs";
import * as S from "./style";
import { useTheme } from "../../../../../../../../../stores";
import { useRootStore } from "../../../../../../../../../stores/hooks";
import { GrayColors, TextColorNames } from "@smartsuite/types";

export const RecordsToFind = observer<{
  inputs: InputCandidate[];
  action: ActionCandidate;
}>(({ inputs, action }) => {
  const theme = useTheme();
  const [value, setValue] = useState<"single" | "multiple">("multiple");
  const [currentAppName, setCurrentAppName] = useState<string | undefined>();
  const [currentSolutionName, setCurrentSolutionName] = useState<
    string | undefined
  >();
  const store = useRootStore();
  const { candidate } = store.automations;

  const getApplicationName = (): string | undefined => {
    const applicationId = findInputAndPick(
      action.inputs,
      INPUT_APPLICATION_ID,
      pickValueConfigString,
    );
    if (applicationId === undefined) {
      return;
    }

    const appsInputDescription = action.description.inputs.find(
      (input) => input.inputId === INPUT_APPLICATION_ID,
    );
    if (
      appsInputDescription === undefined ||
      appsInputDescription.editor?.editor.oneofKind !== "select"
    ) {
      return;
    }

    const application = appsInputDescription.editor?.editor.select.options.find(
      (app) => app.value === applicationId,
    );
    if (application === undefined) {
      return;
    }

    return application.label;
  };

  const getSolutionName = (): string | undefined => {
    const solutionId = findInputAndPick(
      action.inputs,
      INPUT_SOLUTION_ID,
      pickValueConfigString,
    );
    if (solutionId === undefined) {
      return;
    }

    const solutionsInputDescription = action.description.inputs.find(
      (input) => input.inputId === INPUT_SOLUTION_ID,
    );
    if (
      solutionsInputDescription === undefined ||
      solutionsInputDescription.editor?.editor.oneofKind !== "select"
    ) {
      return;
    }

    const solution =
      solutionsInputDescription.editor?.editor.select.options.find(
        (item) => item.value === solutionId,
      );
    if (solution === undefined) {
      return;
    }

    return solution.label;
  };

  const setSearchName = (value: string): void => {
    const actionNameInput = action.inputs.find(
      (i: InputCandidate) => i.inputId === INPUT_ACTION_NAME_ID,
    );
    if (actionNameInput === undefined || actionNameInput.value === undefined) {
      return;
    }
    actionNameInput.value.values = [
      {
        value: {
          oneofKind: "string",
          string: value,
        },
      },
    ];
  };

  const limitInput = inputs.find(
    (item) => item.inputId === FIND_RECORD_LIMIT_ID,
  );

  useEffect(() => {
    if (
      limitInput?.value !== undefined &&
      limitInput.value.values.length > 0 &&
      limitInput.value.values[0].value.oneofKind === FIND_RECORD_LIMIT_SORT_ID
    ) {
      const newValue =
        limitInput.value.values[0].value.limitSort.limit === 1
          ? "single"
          : "multiple";

      const applicationName = getApplicationName();

      if (applicationName === undefined) {
        return;
      }

      if (
        newValue === value &&
        (currentAppName === undefined || applicationName === currentAppName)
      ) {
        setCurrentAppName(applicationName);
      }

      setValue(newValue);
    }
  }, [inputs]);

  useEffect(() => {
    handleChange(value);
  }, [value]);

  const handleChange = (newValue: "single" | "multiple"): void => {
    if (
      limitInput?.value !== undefined &&
      limitInput.value.values.length > 0 &&
      limitInput.value.values[0].value.oneofKind === FIND_RECORD_LIMIT_SORT_ID
    ) {
      const applicationName = getApplicationName();
      if (applicationName === undefined) {
        return;
      }

      const searchNameInput = inputs.find(
        (item) => item.inputId === INPUT_ACTION_NAME_ID,
      );

      if (searchNameInput?.value !== undefined) {
        if (
          searchNameInput.value.values.length === 0 ||
          searchNameInput.value.values[0].value.oneofKind !== "string"
        ) {
          searchNameInput.value.values = [
            {
              value: {
                oneofKind: "string",
                string: "Find Records",
              },
            },
          ];
        }
        if (searchNameInput.value.values[0].value.oneofKind !== "string") {
          return;
        }

        const searchName = searchNameInput.value.values[0].value.string;

        const defaultNames = [
          t("find-records.find-records-default-name"),
          t("find-records.find-records-in", { name: applicationName }),
          ...(currentAppName === undefined
            ? []
            : [t("find-records.find-records-in", { name: currentAppName })]),
          t("find-records.find-one-record-in", { name: applicationName }),
          ...(currentAppName === undefined
            ? []
            : [t("find-records.find-one-record-in", { name: currentAppName })]),
          "",
        ];

        if (newValue === "single") {
          if (defaultNames.includes(searchName)) {
            setSearchName(`Find one Record in ${applicationName}`);
          }
        } else {
          if (defaultNames.includes(searchName)) {
            setSearchName(`Find Records in ${applicationName}`);
          }
        }

        if (applicationName !== undefined) {
          setCurrentAppName(applicationName);
        }
      }
    }
  };

  useEffect(() => {
    const appName = getApplicationName();
    const solutionName = getSolutionName();

    if (currentSolutionName !== solutionName) {
      setCurrentSolutionName(solutionName);
      if (appName === undefined) {
        setSearchName("");
      }
    }

    if (currentAppName !== appName) {
      handleChange(value);
    }
  }, [inputs]);

  if (
    limitInput === undefined ||
    limitInput.value?.values.length === 0 ||
    limitInput.value?.values[0].value.oneofKind !== FIND_RECORD_LIMIT_SORT_ID
  ) {
    return <></>;
  }

  const clearFindOutputsInActions = (action: ActionCandidate): void => {
    candidate.setActionsWithOutReferences(undefined, action.actionReference);
  };

  return (
    <>
      <CaptionBlock
        description={{
          label: t(`caption.records-to-find`),
          icon: "",
          subLabel: "",
          dynamic: false,
        }}
      />
      <S.SwitchWrapper>
        <SwitchButton
          currentValue={value}
          options={[
            {
              value: "multiple",
              label: t(`caption.multiple-records`),
            },
            {
              value: "single",
              label: t(`caption.single-record`),
            },
          ]}
          onChange={(value: string) => {
            if (
              value === "multiple" &&
              limitInput.value !== undefined &&
              limitInput.value.values.length > 0 &&
              limitInput.value.values[0].value.oneofKind ===
                FIND_RECORD_LIMIT_SORT_ID
            ) {
              limitInput.value.values[0].value.limitSort.limit =
                FIND_RECORDS_LIMIT;
              clearFindOutputsInActions(action);
              setValue("multiple");
            } else if (
              value === "single" &&
              limitInput.value !== undefined &&
              limitInput.value.values.length > 0 &&
              limitInput.value.values[0].value.oneofKind ===
                FIND_RECORD_LIMIT_SORT_ID
            ) {
              limitInput.value.values[0].value.limitSort.limit = 1;
              setValue("single");
            }
          }}
          styles={{
            ...stylesSwitchButtonRect,
            height: 28,
            heightSlider: 28,
            gapItem: 5,
            textSize: TextSizes.XS,
            color: TextColorNames.primary,
            colorHover: TextColorNames.primary,
            colorActive: GrayColors["G-0"],
            colorActiveHover: GrayColors["G-0"],
            background: theme.secondaryColor,
          }}
        />
      </S.SwitchWrapper>
    </>
  );
});
